import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Icon,
  Badge,
  Divider,
  useToast
} from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { CheckCircleIcon, InfoIcon, ExternalLinkIcon } from "@chakra-ui/icons";

const Complaints: React.FC = () => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    complaintType: "",
    description: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Placeholder form submission
    toast({
      title: "Complaint Submitted",
      description: "We've received your complaint and will respond within 24 hours.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    setFormData({ name: "", email: "", phone: "", complaintType: "", description: "" });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/complaints"
        fallbackTitle="Complaints"
        fallbackDescription="How to lodge a complaint with One Group"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg="teal.600" color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={5} align="start">
            <Badge colorScheme="teal" fontSize="xs" px={3} py={1} borderRadius="full" bg="teal.400">
              Customer Support
            </Badge>
            <Heading size="2xl" fontWeight="bold">
              Complaints and Feedback
            </Heading>
            <Text fontSize="lg" color="teal.100" maxW="3xl">
              We're committed to providing excellent service. If we've fallen short of your expectations,
              we want to hear from you and make things right.
            </Text>
            <Text fontSize="sm" color="teal.200" mt={2}>
              Last Updated: November 2025
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.lg" py={16}>
        <VStack spacing={12} align="stretch">

          {/* Overview */}
          <Box textAlign="center">
            <Heading size="lg" mb={4} color="gray.800">We Value Your Feedback</Heading>
            <Text color="gray.700" lineHeight="tall" maxW="2xl" mx="auto">
              <strong>[PLACEHOLDER]</strong> Your feedback helps us improve our services. We take all complaints
              seriously and are committed to resolving issues fairly and efficiently. This page outlines our
              complaints handling process and your rights.
            </Text>
          </Box>

          <Divider />

          {/* Process Timeline */}
          <Box>
            <Heading size="lg" mb={6} color="gray.800" textAlign="center">Our Complaints Process</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>

              {/* Step 1 */}
              <Box
                p={6}
                bg="teal.50"
                borderRadius="lg"
                border="2px solid"
                borderColor="teal.200"
                position="relative"
              >
                <Box
                  position="absolute"
                  top={-4}
                  left={6}
                  bg="teal.600"
                  color="white"
                  w={8}
                  h={8}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="bold"
                  fontSize="lg"
                >
                  1
                </Box>
                <VStack align="start" spacing={3} mt={4}>
                  <Heading size="md" color="teal.800">Submit Complaint</Heading>
                  <Text fontSize="sm" color="gray.700">
                    <strong>[PLACEHOLDER]</strong> Contact us via phone, email, or the form below. Provide details
                    about your complaint including dates, relevant documentation, and desired outcome.
                  </Text>
                  <Badge colorScheme="teal" fontSize="xs">Response: 24 hours</Badge>
                </VStack>
              </Box>

              {/* Step 2 */}
              <Box
                p={6}
                bg="teal.50"
                borderRadius="lg"
                border="2px solid"
                borderColor="teal.200"
                position="relative"
              >
                <Box
                  position="absolute"
                  top={-4}
                  left={6}
                  bg="teal.600"
                  color="white"
                  w={8}
                  h={8}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="bold"
                  fontSize="lg"
                >
                  2
                </Box>
                <VStack align="start" spacing={3} mt={4}>
                  <Heading size="md" color="teal.800">Investigation</Heading>
                  <Text fontSize="sm" color="gray.700">
                    <strong>[PLACEHOLDER]</strong> We'll investigate your complaint thoroughly, gather relevant
                    information, and keep you updated on our progress. We may contact you for additional details.
                  </Text>
                  <Badge colorScheme="blue" fontSize="xs">Timeline: 5-15 days</Badge>
                </VStack>
              </Box>

              {/* Step 3 */}
              <Box
                p={6}
                bg="teal.50"
                borderRadius="lg"
                border="2px solid"
                borderColor="teal.200"
                position="relative"
              >
                <Box
                  position="absolute"
                  top={-4}
                  left={6}
                  bg="teal.600"
                  color="white"
                  w={8}
                  h={8}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="bold"
                  fontSize="lg"
                >
                  3
                </Box>
                <VStack align="start" spacing={3} mt={4}>
                  <Heading size="md" color="teal.800">Resolution</Heading>
                  <Text fontSize="sm" color="gray.700">
                    <strong>[PLACEHOLDER]</strong> We'll provide a written response outlining our findings, the
                    action we'll take, and any compensation if applicable. We aim to resolve most complaints quickly.
                  </Text>
                  <Badge colorScheme="green" fontSize="xs">Maximum: 30 days</Badge>
                </VStack>
              </Box>

            </SimpleGrid>
          </Box>

          <Divider />

          {/* Internal Complaints Process */}
          <Box>
            <Heading size="lg" mb={6} color="gray.800">Internal Complaints Process</Heading>
            <VStack align="stretch" spacing={6}>

              <Box p={6} bg="gray.50" borderRadius="md" borderLeft="4px solid" borderColor="teal.500">
                <HStack spacing={3} mb={3}>
                  <Icon as={InfoIcon} color="teal.600" boxSize={5} />
                  <Heading size="sm" color="gray.800">Step 1: Initial Contact</Heading>
                </HStack>
                <Text fontSize="sm" color="gray.700" mb={3}>
                  <strong>[PLACEHOLDER]</strong> Contact our complaints team using any of these methods:
                </Text>
                <VStack align="start" spacing={2} fontSize="sm" color="gray.700" pl={8}>
                  <Text><strong>Email:</strong> complaints@onegroup.com.au</Text>
                  <Text><strong>Phone:</strong> 1300 XXX XXX (Mon-Fri, 9am-5pm AEST)</Text>
                  <Text><strong>Mail:</strong> Complaints Officer, [Placeholder Address], Melbourne VIC 3000</Text>
                  <Text><strong>Online Form:</strong> Use the form below for fastest response</Text>
                </VStack>
                <Box mt={4} p={3} bg="teal.100" borderRadius="md">
                  <Text fontSize="xs" color="gray.700" fontWeight="semibold">
                    We will acknowledge receipt of your complaint within 24 hours
                  </Text>
                </Box>
              </Box>

              <Box p={6} bg="gray.50" borderRadius="md" borderLeft="4px solid" borderColor="teal.500">
                <HStack spacing={3} mb={3}>
                  <Icon as={InfoIcon} color="teal.600" boxSize={5} />
                  <Heading size="sm" color="gray.800">Step 2: Assessment and Investigation</Heading>
                </HStack>
                <Text fontSize="sm" color="gray.700" mb={3}>
                  <strong>[PLACEHOLDER]</strong> Our complaints team will:
                </Text>
                <VStack align="start" spacing={2} fontSize="sm" color="gray.700" pl={8}>
                  <Text>• Assign a dedicated case manager to your complaint</Text>
                  <Text>• Review all relevant documentation and records</Text>
                  <Text>• Interview relevant staff members if necessary</Text>
                  <Text>• Keep you informed of progress throughout the investigation</Text>
                  <Text>• Request additional information from you if needed</Text>
                </VStack>
                <Box mt={4} p={3} bg="teal.100" borderRadius="md">
                  <Text fontSize="xs" color="gray.700" fontWeight="semibold">
                    Most investigations are completed within 5-15 business days
                  </Text>
                </Box>
              </Box>

              <Box p={6} bg="gray.50" borderRadius="md" borderLeft="4px solid" borderColor="teal.500">
                <HStack spacing={3} mb={3}>
                  <Icon as={CheckCircleIcon} color="teal.600" boxSize={5} />
                  <Heading size="sm" color="gray.800">Step 3: Resolution and Response</Heading>
                </HStack>
                <Text fontSize="sm" color="gray.700" mb={3}>
                  <strong>[PLACEHOLDER]</strong> We will provide you with a written response that includes:
                </Text>
                <VStack align="start" spacing={2} fontSize="sm" color="gray.700" pl={8}>
                  <Text>• A summary of your complaint</Text>
                  <Text>• Our findings from the investigation</Text>
                  <Text>• The decision we've reached and reasons for it</Text>
                  <Text>• Any remedial action we will take</Text>
                  <Text>• Your options if you're not satisfied with our response</Text>
                </VStack>
                <Box mt={4} p={3} bg="teal.100" borderRadius="md">
                  <Text fontSize="xs" color="gray.700" fontWeight="semibold">
                    Final response provided within 30 days of receiving your complaint
                  </Text>
                </Box>
              </Box>

            </VStack>
          </Box>

          <Divider />

          {/* External Dispute Resolution */}
          <Box bg="blue.50" p={8} borderRadius="lg" border="2px solid" borderColor="blue.200">
            <Heading size="lg" mb={4} color="blue.900">External Dispute Resolution</Heading>
            <Text color="gray.700" lineHeight="tall" mb={6}>
              <strong>[PLACEHOLDER]</strong> If you're not satisfied with our response, or if we haven't resolved
              your complaint within 30 days, you have the right to refer your complaint to our external dispute
              resolution service:
            </Text>

            <Box bg="white" p={6} borderRadius="md" shadow="sm">
              <Heading size="md" mb={4} color="blue.800">Australian Financial Complaints Authority (AFCA)</Heading>
              <Text fontSize="sm" color="gray.700" mb={4}>
                AFCA is an independent external dispute resolution scheme approved by ASIC. This service is free
                to consumers and small businesses.
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <VStack align="start" spacing={2} fontSize="sm">
                  <Text><strong>Website:</strong> www.afca.org.au</Text>
                  <Text><strong>Phone:</strong> 1800 931 678</Text>
                  <Text><strong>Email:</strong> info@afca.org.au</Text>
                </VStack>
                <VStack align="start" spacing={2} fontSize="sm">
                  <Text><strong>Mail:</strong> GPO Box 3, Melbourne VIC 3001</Text>
                  <Text><strong>Hours:</strong> Monday - Friday, 9am-5pm AEST</Text>
                  <Text><strong>Online Portal:</strong> Available 24/7</Text>
                </VStack>
              </SimpleGrid>

              <Button
                mt={4}
                colorScheme="blue"
                size="sm"
                rightIcon={<ExternalLinkIcon />}
                as="a"
                href="https://www.afca.org.au"
                target="_blank"
              >
                Visit AFCA Website
              </Button>
            </Box>

            <Box mt={6} p={4} bg="yellow.50" borderRadius="md" borderLeft="3px solid" borderColor="yellow.500">
              <Text fontSize="sm" color="gray.700">
                <strong>Time Limits:</strong> Generally, you must lodge a complaint with AFCA within 2 years of
                receiving our final response. Specific time limits may apply depending on the nature of your complaint.
              </Text>
            </Box>
          </Box>

          <Divider />

          {/* Complaint Form */}
          <Box>
            <Heading size="lg" mb={6} color="gray.800" textAlign="center">Submit a Complaint Online</Heading>
            <Box bg="gray.50" p={8} borderRadius="lg" border="1px solid" borderColor="gray.200">
              <Text color="gray.700" mb={6} textAlign="center">
                <strong>[PLACEHOLDER FORM]</strong> Use this form to submit your complaint. We'll acknowledge
                receipt within 24 hours.
              </Text>

              <form onSubmit={handleSubmit}>
                <VStack spacing={5}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} w="full">
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="semibold">Full Name</FormLabel>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        bg="white"
                        placeholder="John Smith"
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="semibold">Email Address</FormLabel>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        bg="white"
                        placeholder="john.smith@example.com"
                      />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} w="full">
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="semibold">Phone Number</FormLabel>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        bg="white"
                        placeholder="04XX XXX XXX"
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="semibold">Complaint Type</FormLabel>
                      <Select
                        name="complaintType"
                        value={formData.complaintType}
                        onChange={handleChange}
                        bg="white"
                        placeholder="Select type"
                      >
                        <option value="service">Service Quality</option>
                        <option value="billing">Billing/Payment Issue</option>
                        <option value="technical">Technical Problem</option>
                        <option value="staff">Staff Conduct</option>
                        <option value="privacy">Privacy Concern</option>
                        <option value="other">Other</option>
                      </Select>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="semibold">Description of Complaint</FormLabel>
                    <Textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      bg="white"
                      rows={6}
                      placeholder="Please provide details about your complaint including relevant dates, what happened, and what outcome you're seeking..."
                    />
                  </FormControl>

                  <Box w="full" textAlign="center">
                    <Button
                      type="submit"
                      colorScheme="teal"
                      size="lg"
                      px={12}
                    >
                      Submit Complaint
                    </Button>
                    <Text fontSize="xs" color="gray.600" mt={3}>
                      By submitting this form, you consent to us processing your personal information to
                      investigate and respond to your complaint.
                    </Text>
                  </Box>
                </VStack>
              </form>
            </Box>
          </Box>

          {/* Additional Information */}
          <Box bg="teal.50" p={6} borderRadius="md" borderLeft="4px solid" borderColor="teal.600">
            <Heading size="sm" mb={3} color="teal.900">Your Rights</Heading>
            <VStack align="start" spacing={2} fontSize="sm" color="gray.700">
              <Text>
                <strong>[PLACEHOLDER]</strong> When lodging a complaint, you have the right to:
              </Text>
              <Text>• Be treated with respect and courtesy</Text>
              <Text>• Have your complaint taken seriously and investigated thoroughly</Text>
              <Text>• Receive regular updates on the progress of your complaint</Text>
              <Text>• Have your personal information kept confidential</Text>
              <Text>• Escalate your complaint if you're not satisfied with our response</Text>
              <Text>• Have a support person or representative assist you</Text>
              <Text>• Access external dispute resolution services at no cost</Text>
            </VStack>
          </Box>

        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Complaints;
